import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { POSTHOG_ENABLED } from "src/app.config";

import ResourceDuration from "@components/Booking/ResourceDuration/ResourceDuration";
import { useLocationContext } from "@contexts/location/LocationContext";
import { DateFormats } from "@enums/shared.enum";
import {
  getPreviousTimeSlots,
  useBackToBackBooking,
} from "@hooks/useBackToBackBooking/useBackToBackBooking";
import { useBooking } from "@hooks/useBooking/useBooking";
import {
  AllowedDurationsMap,
  AvailableTiming,
  ResourceType,
  SingleSlot,
} from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";
import {
  getBookingAttemptId,
  mintBookingAttemptId,
  trackSlotSelected,
} from "@utils/analytics";
import { policyLateBookingWindowMinutes } from "@utils/booking.utils";
import { finalTimeSlotsForDrawer } from "@utils/bookingTimeSlot.utils";
import { formatPrice } from "@utils/common.utils";

const BookingDrawerStepOne = ({
  currency,
  hasValidResource,
  isBookingDisable,
  priceForResource,
  priceLoader,
  resource,
}: {
  isBookingDisable: boolean;
  priceLoader: boolean;
  hasValidResource: boolean;
  resource?: ResourceType;
  priceForResource?: number;
  currency: string;
}) => {
  const {
    backToBackBookingEnabled,
    backToBackBookingSlots,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const {
    getNextStartTime,
    getOccupiedSlots,
    isValidBookingStartTime,
    nextTimes,
    previousTimes,
  } = useBackToBackBooking();
  const [isNotAvailableSlot, setIsNotAvailableSlot] = useState<boolean>(false);
  const [availableSlots, setAvailableSlot] = useState<string>();
  const currentResourceSlots = useMemo(
    () =>
      backToBackBookingSlots?.find(slot => slot.resourceId === resource?.id)
        ?.validAvailableSlots || [],
    [backToBackBookingSlots, resource?.id]
  );

  const drawerBookingData = useSelector(
    (State: RootState) => State.drawer.drawerBookingData
  );

  const resourceTimeSlots = useSelector(
    (State: RootState) => State.drawer.resourceTimeSlots
  );

  const dispatch = useDispatch();
  const { activeLocation } = useLocationContext();

  const { disableTimeMap } = useBooking();

  const changeSelectedTime = useCallback(
    (time: string) => {
      dispatch(bookingSlice.actions.changeSelectedTime(time));
      dispatch(
        drawerSlice.actions.setDrawerBookingData({
          ...drawerBookingData,
          drawerSelectedTime: time,
        })
      );
    },
    [dispatch]
  );
  const timeSlots = useMemo(
    () =>
      finalTimeSlotsForDrawer(
        resourceTimeSlots,
        backToBackBookingEnabled ? selectedTime : drawerBookingData?.drawerTime,
        selectedDate
      ),
    [
      backToBackBookingEnabled,
      resourceTimeSlots,
      selectedTime,
      drawerBookingData?.drawerTime,
      selectedDate,
    ]
  );

  useEffect(() => {
    if (backToBackBookingEnabled) {
      const isToday = selectedDate === moment().format(DateFormats.YMD);
      let occupiedSlots: string[] = [];
      if (resource) {
        occupiedSlots = getOccupiedSlots(resource);
        const hasPreviousOccupiedSlot = previousTimes(
          selectedTime,
          resource?.policyInfo?.bookingStartTimes ?? ["15"]
        ).some(previousTime => occupiedSlots.includes(previousTime));
        if (hasPreviousOccupiedSlot) {
          !isNotAvailableSlot && setAvailableSlot(undefined);
          return;
        }
      }

      if (isToday && resource) {
        const lateBookingWindow = policyLateBookingWindowMinutes(
          resource.policyInfo
        );
        const slotDateTime = moment(
          `${selectedDate}T${selectedTime}`,
          DateFormats.YMDTime
        );
        const isBetweenLateTimeAndCurrentTime =
          slotDateTime.isBefore(moment()) &&
          slotDateTime
            .clone()
            .add(lateBookingWindow, "minutes")
            .isSameOrAfter(moment());

        const nextStartTime = getNextStartTime(
          resource.policyInfo?.bookingStartTimes ?? ["15"],
          0
        );
        const isValidTime = isValidBookingStartTime(
          selectedTime,
          resource?.policyInfo?.bookingStartTimes ?? ["15"]
        );

        const nextSlotTimes = nextTimes(selectedTime);

        const hasOccupiedNextSlot = nextSlotTimes.some(nextTime =>
          occupiedSlots.includes(nextTime)
        );

        if (
          !hasOccupiedNextSlot &&
          isValidTime &&
          (isBetweenLateTimeAndCurrentTime || selectedTime === nextStartTime)
        ) {
          !isNotAvailableSlot && setAvailableSlot(undefined);
          return;
        }
      }

      const isSelectedTimeAvailable =
        currentResourceSlots.includes(selectedTime);
      if (!isSelectedTimeAvailable && currentResourceSlots.length > 0) {
        const slotBefore = [...currentResourceSlots].reverse().find(slot => {
          return moment(
            `${selectedDate}T${slot}`,
            DateFormats.YMDTime
          ).isBefore(
            moment(`${selectedDate}T${selectedTime}`, DateFormats.YMDTime)
          );
        });
        const previousSlot = currentResourceSlots
          .filter(slot =>
            moment(`${selectedDate}T${slot}`, DateFormats.YMDTime).isBefore(
              moment(`${selectedDate}T${selectedTime}`, DateFormats.YMDTime)
            )
          )
          .pop();

        const upcomingSlot =
          slotBefore || previousSlot || currentResourceSlots[0];
        setAvailableSlot(upcomingSlot);
        changeSelectedTime(upcomingSlot);
        setIsNotAvailableSlot(true);
      } else {
        const getPreviousTimeslot = getPreviousTimeSlots(
          resource?.slotInfo?.slots?.allowedDurations as AllowedDurationsMap,
          selectedTime
        );
        const isAvailable = getPreviousTimeslot.some(time =>
          currentResourceSlots.includes(time)
        );
        const isAvailableSlotTime = getPreviousTimeslot.filter(time =>
          currentResourceSlots.includes(time)
        );

        if (isAvailable) {
          setAvailableSlot(isAvailableSlotTime[0]);
          changeSelectedTime(isAvailableSlotTime[0]);
          setIsNotAvailableSlot(true);
        }
        if (
          isAvailableSlotTime.length > 0 &&
          !timeSlots.some(slot => slot.time === currentResourceSlots[0])
        ) {
          setAvailableSlot(isAvailableSlotTime[0]);
          changeSelectedTime(isAvailableSlotTime[0]);
          setIsNotAvailableSlot(true);
        } else {
          !isNotAvailableSlot && setAvailableSlot(undefined);
        }
      }
    } else {
      !isNotAvailableSlot && setAvailableSlot(undefined);
    }
  }, [
    backToBackBookingEnabled,
    resource?.slotInfo?.slots?.allowedDurations,
    selectedTime,
    currentResourceSlots,
    timeSlots,
    selectedDate,
  ]);

  useEffect(() => {
    const isUnavailable = Boolean(backToBackBookingEnabled && availableSlots);
    if (isUnavailable) {
      dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));
    }
  }, [backToBackBookingEnabled, availableSlots, dispatch]);

  const selectSlotFromDrawer = (
    slotDetail: AvailableTiming,
    resourceId: string
  ) => {
    const isSameSlot =
      selectedSlot?.resourceId === resourceId &&
      selectedSlot?.slot?.slot === slotDetail.slot;

    if (isSameSlot) {
      return;
    }
    dispatch(
      bookingSlice.actions.changeSelectedSlot({
        isBookable: true,
        resourceId,
        slot: slotDetail,
      })
    );
    if (POSTHOG_ENABLED) {
      const attemptId = getBookingAttemptId() || mintBookingAttemptId();
      trackSlotSelected({
        booking_attempt_id: attemptId,
        location_id: String(activeLocation?.id || ""),
        resource_id: resourceId,
        slot_duration: Number(slotDetail?.slot) || 0,
        slot_start: drawerBookingData?.drawerTime || selectedTime || "",
      });
    }
  };

  return (
    <>
      {backToBackBookingEnabled && availableSlots && (
        <div className="alert alert-warning small p-2 mt-2">
          {t("bookingStartTimeAvailableAt", {
            selectedTime: availableSlots,
          })}
        </div>
      )}
      <div
        className={`fg-time-bubbles-wrap ${disableTimeMap(selectedSlot || {})}`}
        data-testid="drawer-time-select-test"
      >
        <h6 className="mb-3" data-testid="selected-date">
          {moment(selectedDate).format("DD.MM.YY")}
        </h6>
        <div className="w-100">
          {timeSlots.length > 0 ? (
            <h5 className="mb-3">{t("Drawer.SelectStartTime")}</h5>
          ) : (
            <div className="alert alert-danger small p-2 mt-2">
              {t("noSlotsAvailableForBooking")}
            </div>
          )}
        </div>
        {(backToBackBookingEnabled
          ? timeSlots.filter(slot => slot.time === selectedTime)
          : timeSlots
        ).map(item => (
          <button
            key={item.time}
            className={`btn fg-btn fg-btn-small ${
              item.disabled || isBookingDisable ? "disabled" : ""
            } ${selectedTime === item.time ? "active" : ""}`}
            onClick={() => {
              changeSelectedTime(item.time);
              setIsNotAvailableSlot(false);
            }}
          >
            {item.time}
          </button>
        ))}
      </div>
      {timeSlots.length > 0 && (
        <div className="fg-units-section">
          <h5 className="mb-3">{t("Drawer.SelectDuration")}</h5>
          {hasValidResource && resource ? (
            <ResourceDuration
              key={selectedSlot?.resourceId}
              resource={resource}
              priceLoader={priceLoader}
              isBookingDisable={isBookingDisable}
              selectSlotFromDrawer={selectSlotFromDrawer}
            />
          ) : (
            <div className="alert alert-danger small p-2 mt-2">
              {t("noValidResources")}
            </div>
          )}
        </div>
      )}
      <div className="fg-drawer-time-section total-prize-section mt-2 p-2 bg-default">
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.selectedDate")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>{moment(selectedDate).format("DD.MM.YY")}</h5>
          </div>
        </div>
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.TimeSlot")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>
              {selectedTime || "-"}
              {selectedSlot?.slot?.ends ? " - " + selectedSlot?.slot?.ends : ""}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.BasePrice")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>{formatPrice(priceForResource || 0, currency)}</h5>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDrawerStepOne;
